import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  withDefaults,
} from 'vue';
import { EventType, roomService } from '../../services';
import SvgIcon from '../common/base/SvgIcon.vue';
import CalendarIcon from '../common/icons/CalendarIcon.vue';
import ApplyStageLabelIcon from '../common/icons/ApplyStageLabelIcon.vue';
import ScheduleRoomControl from './ScheduleRoomControl.vue';
import LoadingScheduleIcon from '../common/icons/LoadingScheduleIcon.vue';
import { useI18n } from '../../locales';
import {
  TUIConferenceInfo,
  TUIConferenceListManagerEvents,
  TUIConferenceModifyInfo,
  TUIConferenceStatus,
} from '@tencentcloud/tuiroom-engine-js';
import { isMobile } from '../../utils/environment';
import { objectMerge } from '../../utils/utils.ts';

interface JoinParams {
  roomId: string;
  roomParam?: {
    isOpenCamera?: boolean;
    isOpenMicrophone?: boolean;
  };
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleRoomList',
  props: {
    isShowScheduledConference: { type: Boolean, default: true }
  },
  emits: ["join-conference"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'join-conference', options: JoinParams): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isShowScheduledConference: boolean };

const { t } = useI18n();
const scheduleRoomList = ref<TUIConferenceInfo[]>([]);


const isShowLoading = ref(true);
const joinConference = (options: JoinParams) => {
  emit('join-conference', options);
};



function sortScheduleRoomList() {
  return scheduleRoomList.value.sort(
    (a, b) => a.scheduleStartTime - b.scheduleStartTime
  );
}
const sortedScheduleRoomList = computed(() => sortScheduleRoomList());

const fetchScheduledData = async (cursor = '', result: any[] = []) => {
  const res =
    await roomService.scheduleConferenceManager.fetchScheduledConferenceList({
      cursor,
      count: 20,
    });
  res?.conferenceList && result.push(...(res?.conferenceList || []));
  if (res.cursor !== '') {
    await fetchScheduledData(res.cursor, result);
  }
  return result;
};

const scheduleContentList = computed(() =>
  sortedScheduleRoomList.value.map((item, index, arr) => {
    if (
      index === 0 ||
      getScheduleDate(item.scheduleStartTime) !==
        getScheduleDate(arr[index - 1].scheduleStartTime)
    ) {
      return { ...item, showTimestamp: true };
    }
    return { ...item, showTimestamp: false };
  })
);

function getScheduleDate(timestamp: number) {
  const date = new Date(timestamp * 1000);
  const year = `${date.getFullYear()}`;
  const month = `${date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1}`;
  const day = `${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}`;
  return `${year}${t('schedule year')}${month}${t('schedule month')}${day}${t('schedule day')}`;
}

function getScheduleTime(timestamp: number) {
  const date = new Date(timestamp * 1000);
  const hours = `${date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()}`;
  const minutes = `${date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()}`;
  return `${hours}:${minutes}`;
}

const fetchAttendeeList = async (
  roomId: string,
  cursor = '',
  result: any[] = []
) => {
  const res = await roomService.scheduleConferenceManager.fetchAttendeeList({
    roomId,
    cursor,
    count: 20,
  });
  if (!res?.attendeeList) return [];
  result.push(...(res?.attendeeList || []));
  if (res.cursor !== '') {
    await fetchAttendeeList(roomId, res.cursor, result);
  }
  return result;
};

const handleShowMore = async (data: { roomId: string }) => {
  const attendeeList = await fetchAttendeeList(data.roomId);
  const scheduleRoom = scheduleRoomList.value.find(
    item => item.basicRoomInfo.roomId === data.roomId
  );
  if (scheduleRoom) {
    scheduleRoom.scheduleAttendees = attendeeList;
  }
};

const fetchData = async () => {
  scheduleRoomList.value = await fetchScheduledData();
  isShowLoading.value = false;
};

const updateAttendees = (
  originList: any[] = [],
  changeData: { joinedUsers: any[]; leftUsers: any[] }
) => {
  const { joinedUsers, leftUsers } = changeData;
  // Add joined users to the attendees array
  let resultList = [...originList, ...joinedUsers];
  // Remove left users from the attendees array
  resultList = resultList.filter(
    (attendee: any) =>
      !leftUsers.some(leftUser => leftUser.userId === attendee.userId)
  );
  return resultList;
};
const onConferenceScheduled = async (data: {
  conferenceInfo: TUIConferenceInfo;
}) => {
  const { conferenceInfo } = data;
  const index = scheduleRoomList.value.findIndex(
    item => item.basicRoomInfo.roomId === conferenceInfo.basicRoomInfo.roomId
  );
  if (index >= 0) {
    scheduleRoomList.value.splice(index, 1, conferenceInfo);
  } else {
    scheduleRoomList.value.push(conferenceInfo);
  }
};
const onConferenceCancelled = async (data: any) => {
  scheduleRoomList.value = scheduleRoomList.value.filter(
    item => item.basicRoomInfo.roomId !== data.roomId
  );
};
const onConferenceInfoChanged = async (data: {
  conferenceModifyInfo: TUIConferenceModifyInfo;
}) => {
  const aimConferenceIndex = scheduleRoomList.value.findIndex(
    item =>
      item.basicRoomInfo.roomId ===
      data.conferenceModifyInfo.basicRoomInfo.roomId
  );
  if (aimConferenceIndex !== -1) {
    scheduleRoomList.value[aimConferenceIndex] = objectMerge(
      scheduleRoomList.value[aimConferenceIndex],
      data.conferenceModifyInfo
    );
  }
};
const onScheduleAttendeesChanged = async (data: any) => {
  const aimConference = scheduleRoomList.value.find(
    item => item.basicRoomInfo.roomId === data.roomId
  );
  if (aimConference) {
    aimConference.scheduleAttendees = updateAttendees(
      aimConference.scheduleAttendees,
      data
    );
  }
};

const onConferenceStatusChanged = (data: {
  roomId: string;
  status: TUIConferenceStatus;
}) => {
  const aimConference = scheduleRoomList.value.find(
    item => item.basicRoomInfo.roomId === data.roomId
  );
  if (aimConference) {
    aimConference.status = data.status;
  }
};

onMounted(() => {
  roomService.on(EventType.ROOM_LOGIN, fetchData);
  roomService.scheduleConferenceManager.on(
    TUIConferenceListManagerEvents.onConferenceScheduled,
    onConferenceScheduled
  );
  roomService.scheduleConferenceManager.on(
    TUIConferenceListManagerEvents.onConferenceCancelled,
    onConferenceCancelled
  );
  roomService.scheduleConferenceManager.on(
    TUIConferenceListManagerEvents.onConferenceInfoChanged,
    onConferenceInfoChanged
  );
  roomService.scheduleConferenceManager.on(
    TUIConferenceListManagerEvents.onScheduleAttendeesChanged,
    onScheduleAttendeesChanged
  );
  roomService.scheduleConferenceManager.on(
    TUIConferenceListManagerEvents.onConferenceStatusChanged,
    onConferenceStatusChanged
  );
});
onUnmounted(() => {
  roomService.off(EventType.ROOM_LOGIN, fetchData);
  roomService.scheduleConferenceManager.off(
    TUIConferenceListManagerEvents.onConferenceScheduled,
    onConferenceScheduled
  );
  roomService.scheduleConferenceManager.off(
    TUIConferenceListManagerEvents.onConferenceCancelled,
    onConferenceCancelled
  );
  roomService.scheduleConferenceManager.off(
    TUIConferenceListManagerEvents.onConferenceInfoChanged,
    onConferenceInfoChanged
  );
  roomService.scheduleConferenceManager.off(
    TUIConferenceListManagerEvents.onScheduleAttendeesChanged,
    onScheduleAttendeesChanged
  );
  roomService.scheduleConferenceManager.off(
    TUIConferenceListManagerEvents.onConferenceStatusChanged,
    onConferenceStatusChanged
  );
});

return { __sfc: true,t, scheduleRoomList, emit, isShowLoading, joinConference, props, sortScheduleRoomList, sortedScheduleRoomList, fetchScheduledData, scheduleContentList, getScheduleDate, getScheduleTime, fetchAttendeeList, handleShowMore, fetchData, updateAttendees, onConferenceScheduled, onConferenceCancelled, onConferenceInfoChanged, onScheduleAttendeesChanged, onConferenceStatusChanged, SvgIcon, CalendarIcon, ApplyStageLabelIcon, ScheduleRoomControl, LoadingScheduleIcon, isMobile }
}

})